import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { sharePdfBlob } from "@/lib/downloadPdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import {
  FileText, Plus, Trash2, Calculator, ArrowLeft, MapPin, User, Phone,
  Mail, Calendar, Route, Car, Banknote, Receipt, Locate, Loader2, Share2,
} from "lucide-react";

const VEHICLE_OPTIONS = [
  { key: "sedan", label: "Berline" },
  { key: "premium_sedan", label: "Premium" },
  { key: "van", label: "VAN" },
];

export default function DriverQuotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState("list"); // "list" | "create"

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/driver/quotes");
      setQuotes(data.quotes || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = async (q) => {
    // Un seul bouton : ouvre directement la feuille de partage native
    // (iMessage, Mail, WhatsApp, Enregistrer dans Fichiers…). Depuis là
    // le chauffeur peut visualiser le PDF ET l'envoyer à son client.
    try {
      const res = await api.get(`/driver/quotes/${q.id}.pdf`, { responseType: "blob" });
      await sharePdfBlob(res.data, `BdC-${q.quote_number}.pdf`, {
        title: `Bon de commande ${q.quote_number}`,
        text: `Bon de commande Saor ${q.quote_number} — ${q.client_name}`,
      });
    } catch (e) {
      const msg = String(e?.message || "");
      if (!msg.toLowerCase().includes("abort") && !msg.toLowerCase().includes("cancel")) {
        toast.error(formatApiError(e) || "Impossible d'ouvrir le bon de commande");
      }
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Supprimer le bon de commande ${q.quote_number} ?`)) return;
    try {
      await api.delete(`/driver/quotes/${q.id}`);
      toast.success("Bon de commande supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-4 pb-8 safe-top">
        <div className="mb-6 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            data-testid="quotes-back"
          >
            <ArrowLeft className="h-4 w-4" /> Retour
          </button>
          {view === "list" ? (
            <Button onClick={() => setView("create")} data-testid="quotes-new-btn" className="gap-1.5">
              <Plus className="h-4 w-4" /> Nouveau bon de commande
            </Button>
          ) : (
            <button
              onClick={() => setView("list")}
              className="text-sm text-muted-foreground hover:text-foreground"
              data-testid="quotes-cancel-create"
            >
              Annuler
            </button>
          )}
        </div>

        <header className="mb-6">
          <h1 className="font-[Outfit] text-3xl font-medium tracking-tight">
            <FileText className="inline-block mr-2 h-7 w-7 text-primary -mt-1" />
            Bons de commande
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Créez vos devis sur-mesure (tarif libre ou kilométrique). Génère un PDF prêt à envoyer.
          </p>
        </header>

        {view === "list" ? (
          <QuoteList
            quotes={quotes}
            loading={loading}
            onDownload={downloadPdf}
            onDelete={remove}
            onCreate={() => setView("create")}
          />
        ) : (
          <QuoteForm
            creating={creating}
            setCreating={setCreating}
            onSaved={() => { setView("list"); load(); }}
          />
        )}
      </div>
    </div>
  );
}

function QuoteList({ quotes, loading, onDownload, onDelete, onCreate }) {
  if (loading) {
    return <div className="rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">Chargement…</div>;
  }
  if (quotes.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card/60 p-12 text-center">
        <Receipt className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
        <h3 className="font-[Outfit] text-lg font-medium">Aucun bon de commande pour l'instant</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Créez votre premier devis avec votre propre tarif.
        </p>
        <Button onClick={onCreate} className="mt-4 gap-1.5" data-testid="quotes-empty-create">
          <Plus className="h-4 w-4" /> Créer un bon de commande
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2.5">
      {quotes.map((q) => (
        <div
          key={q.id}
          className="rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary/30"
          data-testid={`quote-row-${q.id}`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="font-mono text-[10px]">{q.quote_number}</Badge>
                <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px]">
                  {q.pricing_mode === "km" ? "Tarif km" : "Forfait"}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {q.created_at ? new Date(q.created_at).toLocaleDateString("fr-FR") : "—"}
                </span>
              </div>
              <h3 className="mt-1 truncate font-[Outfit] text-base">{q.client_name}</h3>
              <p className="text-xs text-muted-foreground truncate">
                {q.departure_address} → {q.arrival_address}
                {q.stops?.length > 0 && ` · ${q.stops.length} arrêt(s)`}
              </p>
            </div>
            <div className="text-right">
              <p className="font-[Outfit] text-2xl font-medium text-primary tabular-nums">
                {q.total_amount?.toFixed(2)} €
              </p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onDownload(q)}
              data-testid={`quote-pdf-${q.id}`}
              className="border-primary/40 text-primary hover:bg-primary/5"
            >
              <Share2 className="mr-1.5 h-3.5 w-3.5" /> Bon de commande
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => onDelete(q)}
              data-testid={`quote-delete-${q.id}`}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Supprimer
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function QuoteForm({ creating, setCreating, onSaved }) {
  const [form, setForm] = useState({
    client_name: "",
    client_phone: "",
    client_email: "",
    service_date: "",
    departure_address: "",
    arrival_address: "",
    stops: [],
    vehicle_type: "sedan",
    passenger_count: 1,
    luggage_count: 0,
    pricing_mode: "flat",
    flat_amount: "",
    pec_amount: "",
    price_per_km: "",
    distance_km: "",
    notes: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // "Utiliser ma position" pour l'adresse de départ — même flow que le
  // widget public : navigateur → geoloc → reverse-geocode via backend.
  const [geoLoading, setGeoLoading] = useState(false);
  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Géolocalisation indisponible sur cet appareil.");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        try {
          const { data } = await api.get(`/geocode/reverse?lng=${lng}&lat=${lat}`);
          set("departure_address", data?.place_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`);
          toast.success("Position détectée");
        } catch {
          set("departure_address", `${lat.toFixed(5)}, ${lng.toFixed(5)}`);
          toast.success("Position détectée (coordonnées GPS)");
        } finally {
          setGeoLoading(false);
        }
      },
      (err) => {
        setGeoLoading(false);
        toast.error(err?.code === 1 ? "Autorisation refusée." : "Impossible d'obtenir la position.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Auto-calcul distance dès que départ + destination sont saisis (mode km).
  // Debounce 800 ms pour ne pas cramer l'API Mapbox à chaque frappe.
  const [distanceLoading, setDistanceLoading] = useState(false);
  useEffect(() => {
    if (form.pricing_mode !== "km") return;
    const dep = form.departure_address.trim();
    const arr = form.arrival_address.trim();
    if (dep.length < 3 || arr.length < 3) return;
    let alive = true;
    setDistanceLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/driver/utils/distance", {
          params: { from_address: dep, to_address: arr },
        });
        if (alive && data?.distance_km != null) {
          set("distance_km", String(data.distance_km));
        }
      } catch (_) {
        // Silencieux — le chauffeur peut toujours saisir la distance à la main
      } finally {
        if (alive) setDistanceLoading(false);
      }
    }, 800);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.departure_address, form.arrival_address, form.pricing_mode]);

  // Live preview of the total
  const previewTotal = (() => {
    if (form.pricing_mode === "flat") {
      const v = parseFloat(form.flat_amount);
      return Number.isFinite(v) ? v : null;
    }
    const pec = parseFloat(form.pec_amount) || 0;
    const ppk = parseFloat(form.price_per_km) || 0;
    const dist = parseFloat(form.distance_km) || 0;
    if (pec === 0 && ppk === 0) return null;
    return pec + ppk * dist;
  })();

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        client_name: form.client_name.trim(),
        client_phone: form.client_phone || null,
        client_email: form.client_email || null,
        service_date: form.service_date || null,
        departure_address: form.departure_address,
        arrival_address: form.arrival_address,
        stops: (form.stops || []).filter(Boolean).map((s) => ({ address: s })),
        vehicle_type: form.vehicle_type,
        passenger_count: Number(form.passenger_count) || 1,
        luggage_count: Number(form.luggage_count) || 0,
        pricing_mode: form.pricing_mode,
        notes: form.notes,
      };
      if (form.pricing_mode === "flat") {
        payload.flat_amount = parseFloat(form.flat_amount);
      } else {
        payload.pec_amount = parseFloat(form.pec_amount) || 0;
        payload.price_per_km = parseFloat(form.price_per_km) || 0;
        payload.distance_km = parseFloat(form.distance_km) || 0;
      }
      await api.post("/driver/quotes", payload);
      toast.success("Bon de commande créé");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="quote-form">
      {/* Client block */}
      <section className="rounded-2xl border border-border bg-card p-5 space-y-3">
        <h2 className="font-[Outfit] text-sm font-medium uppercase tracking-widest text-muted-foreground">
          <User className="inline-block h-3.5 w-3.5 mr-1" /> Client
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Nom *</Label>
            <Input required value={form.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="quote-client-name" />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3" /> Date de la prestation</Label>
            <Input type="datetime-local" value={form.service_date} onChange={(e) => set("service_date", e.target.value)} data-testid="quote-service-date" />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><Phone className="h-3 w-3" /> Téléphone <span className="text-muted-foreground/70">(optionnel)</span></Label>
            <Input value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} data-testid="quote-client-phone" />
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> Email <span className="text-muted-foreground/70">(optionnel)</span></Label>
            <Input type="email" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} data-testid="quote-client-email" />
          </div>
        </div>
      </section>

      {/* Trip block */}
      <section className="rounded-2xl border border-border bg-card p-5 space-y-3">
        <h2 className="font-[Outfit] text-sm font-medium uppercase tracking-widest text-muted-foreground">
          <Route className="inline-block h-3.5 w-3.5 mr-1" /> Trajet
        </h2>
        <div>
          <Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> Départ *</Label>
          <AddressAutocomplete value={form.departure_address} onChange={(v) => set("departure_address", v)} required data-testid="quote-departure" />
          <button
            type="button"
            onClick={useCurrentLocation}
            disabled={geoLoading}
            className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 px-3 py-1 text-[11px] font-medium text-primary hover:bg-primary/10 disabled:opacity-50"
            data-testid="quote-geolocate"
          >
            {geoLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Locate className="h-3 w-3" />}
            Utiliser ma position actuelle
          </button>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Arrêts intermédiaires ({form.stops.length})</Label>
            {form.stops.length < 5 && (
              <button type="button" onClick={() => set("stops", [...form.stops, ""])} className="text-xs text-primary hover:underline" data-testid="quote-stop-add">
                + Ajouter un arrêt
              </button>
            )}
          </div>
          {form.stops.length > 0 && (
            <div className="mt-1 space-y-1.5">
              {form.stops.map((s, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">{i + 1}</span>
                  <div className="flex-1">
                    <AddressAutocomplete
                      value={s}
                      onChange={(v) => { const next = [...form.stops]; next[i] = v; set("stops", next); }}
                      placeholder={`Adresse de l'arrêt ${i + 1}…`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => set("stops", form.stops.filter((_, idx) => idx !== i))}
                    className="grid h-7 w-7 flex-none place-items-center rounded-md text-destructive hover:bg-destructive/10"
                    data-testid={`quote-stop-remove-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> Destination *</Label>
          <AddressAutocomplete value={form.arrival_address} onChange={(v) => set("arrival_address", v)} required data-testid="quote-arrival" />
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><Car className="h-3 w-3" /> Catégorie de véhicule</Label>
          <div className="mt-1 grid grid-cols-3 gap-1.5">
            {VEHICLE_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                onClick={() => set("vehicle_type", o.key)}
                data-testid={`quote-vehicle-${o.key}`}
                className={`rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                  form.vehicle_type === o.key
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card hover:border-primary/40"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Passagers</Label>
            <Input type="number" min="1" max="20" value={form.passenger_count} onChange={(e) => set("passenger_count", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Bagages</Label>
            <Input type="number" min="0" max="20" value={form.luggage_count} onChange={(e) => set("luggage_count", e.target.value)} />
          </div>
        </div>
      </section>

      {/* Pricing block */}
      <section className="rounded-2xl border border-border bg-card p-5 space-y-3">
        <h2 className="font-[Outfit] text-sm font-medium uppercase tracking-widest text-muted-foreground">
          <Banknote className="inline-block h-3.5 w-3.5 mr-1" /> Tarification
        </h2>
        <div className="inline-flex w-full rounded-lg border border-border bg-secondary/30 p-0.5 text-xs">
          {[
            { key: "flat", label: "Forfait libre" },
            { key: "km", label: "Tarif kilométrique" },
          ].map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => set("pricing_mode", m.key)}
              data-testid={`quote-pricing-mode-${m.key}`}
              className={`flex-1 px-3 py-1.5 rounded-md transition-colors ${
                form.pricing_mode === m.key ? "bg-primary text-primary-foreground shadow-sm" : "text-foreground/70 hover:text-foreground"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {form.pricing_mode === "flat" ? (
          <div>
            <Label className="text-xs">Montant forfaitaire (€) *</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              required
              value={form.flat_amount}
              onChange={(e) => set("flat_amount", e.target.value)}
              placeholder="Ex. 150"
              data-testid="quote-flat-amount"
            />
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">PEC (€)</Label>
              <Input type="number" step="0.01" min="0" value={form.pec_amount} onChange={(e) => set("pec_amount", e.target.value)} placeholder="35" data-testid="quote-pec" />
            </div>
            <div>
              <Label className="text-xs">€ / km</Label>
              <Input type="number" step="0.01" min="0" value={form.price_per_km} onChange={(e) => set("price_per_km", e.target.value)} placeholder="2,50" data-testid="quote-ppk" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">
                Distance (km)
                {distanceLoading && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
              </Label>
              <Input type="number" step="0.01" min="0" value={form.distance_km} onChange={(e) => set("distance_km", e.target.value)} placeholder="25" data-testid="quote-distance" />
              <p className="mt-1 text-[10px] text-muted-foreground">
                Auto-calculé dès que les adresses sont saisies — modifiable manuellement.
              </p>
            </div>
          </div>
        )}

        <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 flex items-center gap-3" data-testid="quote-total-preview">
          <Calculator className="h-5 w-5 text-primary" />
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total à facturer</p>
            <p className="font-[Outfit] text-2xl font-medium text-primary tabular-nums">
              {previewTotal != null ? `${previewTotal.toFixed(2)} €` : "—"}
            </p>
          </div>
        </div>
      </section>

      {/* Notes */}
      <section className="rounded-2xl border border-border bg-card p-5">
        <Label className="text-xs">Notes (optionnel)</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          placeholder="Conditions particulières, prestations incluses…"
          rows={3}
          data-testid="quote-notes"
        />
      </section>

      <Button
        type="submit"
        disabled={creating}
        className="w-full gap-1.5 bg-primary hover:bg-primary/90"
        data-testid="quote-submit"
      >
        {creating ? "Création…" : <><FileText className="h-4 w-4" /> Créer le bon de commande</>}
      </Button>
    </form>
  );
}
